import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Button,
  Avatar,
  Chip,
  Snackbar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Edit,
  Save,
  Close,
  Logout,
  Person,
  Email,
  CalendarToday,
  LocalFireDepartment,
  MoodOutlined,
  EmojiEvents,
  Star,
  Phone,
} from '@mui/icons-material';
import { getMyProfile, updateProfile, getMyStreaks, getMoodSummary, mpesaPay } from '../services/api';
import { useNavigate } from 'react-router-dom';

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}
function capitalizeName(name) {
  if (!name) return '';
  return name.split(' ').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
}
function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-KE', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}
const AVATAR_COLORS = ['#4ade80','#22d3ee','#a78bfa','#f472b6','#fbbf24','#fb923c'];
function avatarColor(name = '') {
  let hash = 0;
  for (let c of name) hash = c.charCodeAt(0) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function StatCard({ icon, label, value, color }) {
  return (
    <Box sx={{
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 3, p: 2, textAlign: 'center', flex: 1,
    }}>
      <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
      <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#fff' }}>
        {value ?? '—'}
      </Typography>
      <Typography sx={{ fontSize: '0.7rem', color: '#7fa8c8', mt: 0.25 }}>{label}</Typography>
    </Box>
  );
}

const PLUS_FEATURES = [
  'Detailed mood analytics & reports',
  'Unlimited voice journal entries',
  'Exclusive devotional content',
  'Priority community access',
  'Personalized streak challenges',
];

export default function Profile() {
  const navigate = useNavigate();

  const [profile,   setProfile]   = useState(null);
  const [stats,     setStats]     = useState({ streak: 0, moods: 0, longest: 0 });
  const [loading,   setLoading]   = useState(true);
  const [editing,   setEditing]   = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const [toast,     setToast]     = useState('');

  // Imara Plus modal
  const [plusOpen,    setPlusOpen]    = useState(false);
  const [phoneNum,    setPhoneNum]    = useState('');
  const [paying,      setPaying]      = useState(false);
  const [payError,    setPayError]    = useState('');
  const [paySuccess,  setPaySuccess]  = useState(false);

  // Editable fields
  const [formName,  setFormName]  = useState('');
  const [formBio,   setFormBio]   = useState('');
  const [formPhone, setFormPhone] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/'); return; }
    loadAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadAll() {
    try {
      setLoading(true); setError('');
      const [profileRes, streakRes, moodRes] = await Promise.all([
        getMyProfile(),
        getMyStreaks().catch(() => ({ data: [] })),
        getMoodSummary().catch(() => ({ data: null })),
      ]);
      const p = profileRes.data;
      setProfile(p);
      setFormName(p?.name ?? p?.username ?? '');
      setFormBio(p?.bio ?? '');
      setFormPhone(p?.phone ?? '');

      const streakList = Array.isArray(streakRes.data)
        ? streakRes.data : streakRes.data?.streaks ?? [];
      const currentStreak = streakList[0]?.current_streak ?? streakList[0]?.streak ?? 0;
      const longestStreak = streakList[0]?.longest_streak ?? streakList[0]?.longest ?? 0;
      const moodData  = moodRes.data;
      const moodCount = moodData?.total ?? moodData?.count ?? moodData?.total_checkins ?? 0;
      setStats({ streak: currentStreak, longest: longestStreak, moods: moodCount });
    } catch {
      setError('Could not load your profile. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true); setError('');
    try {
      const payload = {
        name:  formName.trim()  || undefined,
        bio:   formBio.trim()   || undefined,
        phone: formPhone.trim() || undefined,
      };
      const res = await updateProfile(payload);
      setProfile(prev => ({ ...prev, ...res.data }));
      setEditing(false);
      setToast('Profile updated! ✅');
    } catch {
      setError('Could not save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handlePay() {
    if (!phoneNum.trim()) {
      setPayError('Please enter your M-Pesa phone number.');
      return;
    }
    try {
      setPaying(true);
      setPayError('');
      await mpesaPay({
        phone_number: phoneNum.trim(),
        amount: 200,
        description: 'Imara Plus Subscription',
      });
      setPaySuccess(true);
    } catch (err) {
      setPayError(
        err?.response?.data?.detail ?? 'Payment failed. Please try again.'
      );
    } finally {
      setPaying(false);
    }
  }

  function closePlusModal() {
    setPlusOpen(false);
    setPhoneNum('');
    setPayError('');
    setPaySuccess(false);
  }

  function handleLogout() {
    localStorage.removeItem('token');
    navigate('/');
  }
  function cancelEdit() {
    setFormName(profile?.name ?? profile?.username ?? '');
    setFormBio(profile?.bio ?? '');
    setFormPhone(profile?.phone ?? '');
    setEditing(false);
    setError('');
  }

  const displayName = capitalizeName(profile?.name ?? profile?.username ?? 'You');
  const email       = profile?.email ?? '';
  const joinDate    = profile?.created_at ?? profile?.joined_at ?? '';
  const color       = avatarColor(displayName);

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a1628 0%, #0d1f3c 50%, #0a2040 100%)',
      px: { xs: 2, sm: 4 },
      py: 4,
      color: '#e8f4f8',
    }}>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography sx={{
            fontSize: { xs: '1.8rem', sm: '2.2rem' },
            fontWeight: 800,
            background: 'linear-gradient(90deg, #4ade80, #22d3ee)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.5px',
          }}>
            Profile
          </Typography>
          <Typography sx={{ color: '#7fa8c8', fontSize: '0.9rem', mt: 0.5 }}>
            Your Imara journey 🌱
          </Typography>
        </Box>
        <Button
          onClick={handleLogout}
          startIcon={<Logout />}
          sx={{
            color: '#7fa8c8', textTransform: 'none', fontSize: '0.85rem',
            border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, px: 2,
            '&:hover': { color: '#ef9a9a', borderColor: 'rgba(239,154,154,0.3)', background: 'rgba(239,154,154,0.05)' },
          }}
        >
          Logout
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress sx={{ color: '#4ade80' }} />
        </Box>
      )}
      {error && !loading && (
        <Alert severity="error" sx={{
          mb: 3, background: 'rgba(211,47,47,0.12)',
          color: '#ef9a9a', border: '1px solid rgba(211,47,47,0.3)',
        }}>
          {error}
        </Alert>
      )}

      {!loading && profile && (
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>

          {/* Avatar + name card */}
          <Box sx={{
            background: 'linear-gradient(135deg, rgba(74,222,128,0.08), rgba(34,211,238,0.04))',
            border: '1px solid rgba(74,222,128,0.2)',
            borderRadius: 4, p: { xs: 3, sm: 4 }, mb: 3,
            textAlign: 'center', position: 'relative',
          }}>
            {!editing ? (
              <Button onClick={() => setEditing(true)} startIcon={<Edit />} size="small" sx={{
                position: 'absolute', top: 16, right: 16, color: '#4ade80',
                textTransform: 'none', fontSize: '0.8rem',
                background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.25)',
                borderRadius: 2, '&:hover': { background: 'rgba(74,222,128,0.2)' },
              }}>Edit</Button>
            ) : (
              <Button onClick={cancelEdit} startIcon={<Close />} size="small" sx={{
                position: 'absolute', top: 16, right: 16, color: '#7fa8c8',
                textTransform: 'none', fontSize: '0.8rem',
                border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2,
              }}>Cancel</Button>
            )}

            <Avatar sx={{
              width: 80, height: 80, background: `${color}22`, color,
              fontSize: '1.8rem', fontWeight: 700, border: `2px solid ${color}44`,
              mx: 'auto', mb: 2,
            }}>
              {getInitials(displayName)}
            </Avatar>

            {!editing && (
              <>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#fff', mb: 0.5 }}>
                  {displayName}
                </Typography>
                {profile?.bio && (
                  <Typography sx={{ fontSize: '0.9rem', color: '#7fa8c8', mb: 1.5, fontStyle: 'italic' }}>
                    {profile.bio}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'center' }}>
                  {email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Email sx={{ fontSize: 14, color: '#7fa8c8' }} />
                      <Typography sx={{ fontSize: '0.85rem', color: '#7fa8c8' }}>{email}</Typography>
                    </Box>
                  )}
                  {profile?.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Person sx={{ fontSize: 14, color: '#7fa8c8' }} />
                      <Typography sx={{ fontSize: '0.85rem', color: '#7fa8c8' }}>{profile.phone}</Typography>
                    </Box>
                  )}
                  {joinDate && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <CalendarToday sx={{ fontSize: 14, color: '#7fa8c8' }} />
                      <Typography sx={{ fontSize: '0.8rem', color: '#7fa8c8' }}>
                        Joined {formatDate(joinDate)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </>
            )}

            {editing && (
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'left' }}>
                {[
                  { label: 'Name', value: formName, setter: setFormName, placeholder: 'Your name' },
                  { label: 'Bio', value: formBio, setter: setFormBio, placeholder: 'A short bio…', multiline: true },
                  { label: 'Phone', value: formPhone, setter: setFormPhone, placeholder: '+254 7XX XXX XXX' },
                ].map(field => (
                  <TextField
                    key={field.label}
                    label={field.label}
                    value={field.value}
                    onChange={e => field.setter(e.target.value)}
                    placeholder={field.placeholder}
                    fullWidth size="small"
                    multiline={field.multiline} rows={field.multiline ? 3 : 1}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(255,255,255,0.04)', color: '#e8f4f8', borderRadius: 2,
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                        '&:hover fieldset': { borderColor: 'rgba(74,222,128,0.3)' },
                        '&.Mui-focused fieldset': { borderColor: '#4ade80' },
                      },
                      '& .MuiInputLabel-root': { color: '#7fa8c8' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#4ade80' },
                    }}
                  />
                ))}
                <Button
                  onClick={handleSave} disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} sx={{ color: '#0a1628' }} /> : <Save />}
                  sx={{
                    background: 'linear-gradient(135deg, #4ade80, #22d3ee)',
                    color: '#0a1628', fontWeight: 700, textTransform: 'none',
                    borderRadius: 2, py: 1,
                    '&:hover': { opacity: 0.85 },
                    '&.Mui-disabled': { background: 'rgba(255,255,255,0.08)', color: '#3a5a7a' },
                  }}
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </Button>
              </Box>
            )}
          </Box>

          {/* Stats */}
          <Typography sx={{ fontWeight: 700, mb: 2, color: '#e8f4f8' }}>Your Stats</Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <StatCard icon={<LocalFireDepartment sx={{ fontSize: 24 }} />} label="Current Streak" value={`${stats.streak}d`} color="#ff6b35" />
            <StatCard icon={<EmojiEvents sx={{ fontSize: 24 }} />} label="Longest Streak" value={`${stats.longest}d`} color="#fbbf24" />
            <StatCard icon={<MoodOutlined sx={{ fontSize: 24 }} />} label="Mood Check-ins" value={stats.moods} color="#4ade80" />
          </Box>

          {/* Account section */}
          <Box sx={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 3, overflow: 'hidden', mb: 3,
          }}>
            <Typography sx={{ px: 2.5, py: 1.5, fontSize: '0.75rem', fontWeight: 700, color: '#7fa8c8', textTransform: 'uppercase', letterSpacing: 1.5 }}>
              Account
            </Typography>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
            {[
              { label: 'Email', value: email || 'Not set' },
              { label: 'Account type', value: profile?.role ?? profile?.type ?? 'Standard' },
              { label: 'Member since', value: formatDate(joinDate) },
            ].map((item, i) => (
              <Box key={i}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, py: 1.75 }}>
                  <Typography sx={{ fontSize: '0.88rem', color: '#7fa8c8' }}>{item.label}</Typography>
                  <Typography sx={{ fontSize: '0.88rem', color: '#e8f4f8', fontWeight: 500 }}>{item.value}</Typography>
                </Box>
                {i < 2 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.04)' }} />}
              </Box>
            ))}
          </Box>

          {/* Wellness badge */}
          <Box sx={{
            background: 'rgba(74,222,128,0.06)',
            border: '1px solid rgba(74,222,128,0.15)',
            borderRadius: 3, p: 2.5,
            display: 'flex', alignItems: 'center', gap: 2, mb: 3,
          }}>
            <Typography sx={{ fontSize: '2rem' }}>🌱</Typography>
            <Box>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#4ade80' }}>
                Imara Wellness Member
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#7fa8c8' }}>
                Growing stronger, one day at a time
              </Typography>
            </Box>
            <Chip label="Active" size="small" sx={{
              ml: 'auto',
              background: 'rgba(74,222,128,0.15)', color: '#4ade80',
              border: '1px solid rgba(74,222,128,0.3)', fontSize: '0.7rem',
            }} />
          </Box>

          {/* ── Imara Plus Card ── */}
          <Box sx={{
            background: 'linear-gradient(135deg, rgba(251,191,36,0.08), rgba(251,191,36,0.03))',
            border: '1px solid rgba(251,191,36,0.25)',
            borderRadius: 3, p: 3, mb: 3,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Star sx={{ color: '#fbbf24', fontSize: 22 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#fbbf24' }}>
                Upgrade to Imara Plus
              </Typography>
              <Chip label="KES 200/mo" size="small" sx={{
                ml: 'auto',
                background: 'rgba(251,191,36,0.15)', color: '#fbbf24',
                border: '1px solid rgba(251,191,36,0.3)', fontSize: '0.7rem', fontWeight: 700,
              }} />
            </Box>
            <Typography sx={{ color: '#7fa8c8', fontSize: '0.82rem', mb: 2 }}>
              Unlock premium features to supercharge your wellness journey.
            </Typography>

            {/* Features list */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2.5 }}>
              {PLUS_FEATURES.map((f, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: '#fbbf24', flexShrink: 0,
                  }} />
                  <Typography sx={{ fontSize: '0.82rem', color: '#e8f4f8' }}>{f}</Typography>
                </Box>
              ))}
            </Box>

            <Button
              fullWidth
              onClick={() => setPlusOpen(true)}
              startIcon={<Star />}
              sx={{
                background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                color: '#0a1628', fontWeight: 700, textTransform: 'none',
                borderRadius: 2, py: 1.2, fontSize: '0.9rem',
                '&:hover': { opacity: 0.85 },
              }}
            >
              Get Imara Plus
            </Button>
          </Box>

          {/* Logout button */}
          <Button
            fullWidth onClick={handleLogout} startIcon={<Logout />}
            sx={{
              color: '#ef9a9a', textTransform: 'none', fontWeight: 600,
              border: '1px solid rgba(239,154,154,0.2)', borderRadius: 2, py: 1.2,
              '&:hover': { background: 'rgba(239,154,154,0.06)', borderColor: 'rgba(239,154,154,0.4)' },
            }}
          >
            Log Out
          </Button>
        </Box>
      )}

      {/* ── Imara Plus Payment Modal ── */}
      <Dialog
        open={plusOpen}
        onClose={closePlusModal}
        PaperProps={{
          sx: {
            background: '#0d1f3c',
            border: '1px solid rgba(251,191,36,0.2)',
            borderRadius: 3,
            color: '#e8f4f8',
            minWidth: { xs: '90vw', sm: 400 },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#fbbf24', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Star sx={{ fontSize: 20 }} />
            Imara Plus — KES 200/month
          </Box>
        </DialogTitle>

        <DialogContent>
          {!paySuccess ? (
            <Box>
              <Typography sx={{ color: '#7fa8c8', fontSize: '0.875rem', mb: 2.5 }}>
                Enter your M-Pesa phone number. You'll receive a payment prompt on your phone.
              </Typography>

              <TextField
                fullWidth
                label="M-Pesa Phone Number"
                placeholder="07XXXXXXXX"
                value={phoneNum}
                onChange={e => setPhoneNum(e.target.value)}
                InputProps={{ startAdornment: <Phone sx={{ color: '#7fa8c8', mr: 1, fontSize: 18 }} /> }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255,255,255,0.04)', color: '#e8f4f8', borderRadius: 2,
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                    '&:hover fieldset': { borderColor: 'rgba(251,191,36,0.3)' },
                    '&.Mui-focused fieldset': { borderColor: '#fbbf24' },
                  },
                  '& .MuiInputLabel-root': { color: '#7fa8c8' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#fbbf24' },
                }}
              />

              {payError && (
                <Alert severity="error" sx={{
                  mt: 2, background: 'rgba(239,68,68,0.08)',
                  color: '#fca5a5', border: '1px solid rgba(239,68,68,0.2)',
                  fontSize: '0.82rem',
                }}>
                  {payError}
                </Alert>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography sx={{ fontSize: '2.5rem', mb: 1 }}>🎉</Typography>
              <Typography sx={{ fontWeight: 700, color: '#4ade80', fontSize: '1.1rem', mb: 1 }}>
                Payment prompt sent!
              </Typography>
              <Typography sx={{ color: '#7fa8c8', fontSize: '0.875rem' }}>
                Check your phone and enter your M-Pesa PIN to complete your Imara Plus subscription.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={closePlusModal}
            sx={{ color: '#7fa8c8', textTransform: 'none', borderRadius: 2 }}
          >
            {paySuccess ? 'Close' : 'Cancel'}
          </Button>
          {!paySuccess && (
            <Button
              onClick={handlePay}
              disabled={paying}
              startIcon={paying ? <CircularProgress size={16} sx={{ color: '#0a1628' }} /> : <Star />}
              sx={{
                background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                color: '#0a1628', fontWeight: 700, textTransform: 'none',
                borderRadius: 2, px: 3,
                '&:hover': { opacity: 0.85 },
                '&.Mui-disabled': { background: 'rgba(255,255,255,0.08)', color: '#3a5a7a' },
              }}
            >
              {paying ? 'Processing…' : 'Pay KES 200'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={!!toast} autoHideDuration={3000} onClose={() => setToast('')}
        message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            background: 'rgba(74,222,128,0.15)', color: '#4ade80',
            border: '1px solid rgba(74,222,128,0.3)', borderRadius: 2,
          },
        }}
      />
    </Box>
  );
}